import { useState } from "react";
import { C, FONT, acGlow, logoDot } from "../theme";
import { Dots } from "../components/Dots";
import { useToast } from "../components/Toast";
import { createCheckout } from "../lib/api";
import { PRICING, planTotal, type Period } from "../lib/pricing";
import { isFeatureEnabled } from "../lib/featureFlags";

// TEMPORARY (awaiting tax ID): while `subscribe-buttons` is OFF, the payment
// button is hidden (kept in the DOM) and a "contact us" link shows instead.
const CONTACT_HREF = "mailto:contact@zynday.com";

type Step = "pay" | "processing" | "done";

export function Checkout({
  period,
  email,
  setEmail,
  onBack,
  onContinueToLogin,
  preview = false,
}: {
  period: Period;
  email: string;
  setEmail: (v: string) => void;
  onBack: () => void;
  onContinueToLogin: () => void;
  // preview=true runs the local fake-Stripe animation instead of redirecting,
  // so the design can be demoed without a live Stripe account.
  preview?: boolean;
}) {
  const [step, setStep] = useState<Step>("pay");
  const showSubscribe = isFeatureEnabled("subscribe-buttons");
  const toast = useToast();
  const plan = PRICING[period];
  const total = `€${planTotal(period)}.00`;

  const payNow = async () => {
    if (!/.+@.+\..+/.test(email)) {
      toast("Enter a valid email address");
      return;
    }
    if (preview) {
      setStep("processing");
      setTimeout(() => setStep("done"), 1600);
      return;
    }
    setStep("processing");
    try {
      const { url } = await createCheckout(email, period);
      window.location.href = url; // hand off to Stripe Checkout
    } catch (e) {
      setStep("pay");
      toast(e instanceof Error ? e.message : "Checkout failed");
    }
  };

  const wrap: React.CSSProperties = {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 40,
  };

  return (
    <div style={wrap}>
      <div style={{ width: 440, maxWidth: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 11, justifyContent: "center", fontWeight: 700, color: C.textBright, fontSize: 16, marginBottom: 26 }}>
          <span style={logoDot} />geo.zynday.com
        </div>

        {step === "pay" && (
          <div style={{ background: C.panel, border: `1px solid ${C.line2}`, borderRadius: 12, overflow: "hidden", animation: "fadeup .3s ease" }}>
            <div style={{ padding: "24px 26px", borderBottom: `1px solid ${C.line}` }}>
              <div style={{ color: C.muted, fontSize: 12, marginBottom: 6 }}>Subscribe to</div>
              <div style={{ color: C.textBright, fontWeight: 700, fontSize: 17 }}>geo.zynday.com Pro · {plan.label}</div>
              <div style={{ color: C.mutedDim, fontSize: 12, marginTop: 4 }}>{plan.sub} · cancel anytime</div>
            </div>
            <div style={{ padding: "24px 26px" }}>
              <label style={{ color: C.muted, fontSize: 12, display: "block", marginBottom: 7 }}>Email</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                style={{ width: "100%", background: C.bg, border: `1px solid ${C.line3}`, borderRadius: 7, padding: 12, color: C.textBrightest, font: `500 13px ${FONT}`, marginBottom: 18 }}
              />
              {!showSubscribe && (
                <a href={CONTACT_HREF} style={{ display: "block", width: "100%", background: C.ac, color: C.acInk, padding: 14, borderRadius: 8, font: `700 14px ${FONT}`, cursor: "pointer", boxShadow: acGlow(0.3), textDecoration: "none", textAlign: "center", boxSizing: "border-box" }}>
                  Contact us
                </a>
              )}
              <button onClick={payNow} style={{ width: "100%", background: C.ac, border: "none", color: C.acInk, padding: 14, borderRadius: 8, font: `700 14px ${FONT}`, cursor: "pointer", boxShadow: acGlow(0.3), display: showSubscribe ? undefined : "none" }}>
                {preview ? `Pay ${total}` : <>Continue to payment · {total} <Dots /></>}
              </button>
              <div style={{ textAlign: "center", color: C.mutedDim, fontSize: 11, marginTop: 14 }}>🔒 {preview ? "Test checkout" : "Card details entered securely on Stripe"} · Powered by Stripe</div>
              <div style={{ textAlign: "center", marginTop: 14 }}>
                <button onClick={onBack} style={{ background: "none", border: "none", color: C.muted, font: `400 12px ${FONT}`, cursor: "pointer", textDecoration: "underline" }}>← back to site</button>
              </div>
            </div>
          </div>
        )}

        {step === "processing" && (
          <div style={{ background: C.panel, border: `1px solid ${C.line2}`, borderRadius: 12, padding: "60px 26px", textAlign: "center", animation: "fadeup .3s ease" }}>
            <Spinner />
            <div style={{ color: C.textBright, fontWeight: 600 }}>Processing payment…</div>
            <div style={{ color: C.muted, fontSize: 13, marginTop: 8 }}>Talking to Stripe</div>
          </div>
        )}

        {step === "done" && (
          <div style={{ background: C.panel, border: `1px solid ${C.line3}`, borderRadius: 12, padding: "50px 30px", textAlign: "center", boxShadow: "0 0 40px rgba(54,224,127,.08)", animation: "fadeup .3s ease" }}>
            <div style={{ width: 56, height: 56, border: `2px solid ${C.ac}`, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 22px", color: C.ac, fontSize: 26, boxShadow: "0 0 24px rgba(54,224,127,.25)" }}>✓</div>
            <div style={{ color: C.textBrightest, fontWeight: 700, fontSize: 20, marginBottom: 10 }}>You're subscribed</div>
            <div style={{ color: C.mutedSoft, fontSize: 13, lineHeight: 1.7, marginBottom: 28 }}>
              We emailed a one-time login code to<br />
              <span style={{ color: C.text }}>{email || "your inbox"}</span>
            </div>
            <button onClick={onContinueToLogin} style={{ width: "100%", background: C.ac, border: "none", color: C.acInk, padding: 14, borderRadius: 8, font: `700 14px ${FONT}`, cursor: "pointer", boxShadow: acGlow(0.3) }}>Continue to login <Dots /></button>
          </div>
        )}
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ width: 34, height: 34, border: `3px solid ${C.line2}`, borderTopColor: C.acRaw, borderRadius: "50%", margin: "0 auto 22px", animation: "spin .8s linear infinite" }} />
  );
}
